import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { categoryMeta } from "@/constants/content";
import { formatDate, formatViews } from "@/utils/format";

interface ArticleCardProps {
  article: Article;
  layout?: "grid" | "row";
}

export default function ArticleCard({ article, layout = "grid" }: ArticleCardProps) {
  const meta = categoryMeta[article.category];

  if (layout === "row") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex flex-col gap-4 rounded-lg border border-background-800/80 bg-background-900/60 p-3 transition-colors hover:border-primary-700/70 hover:bg-background-900 sm:flex-row"
      >
        <div className="relative h-40 w-full shrink-0 overflow-hidden rounded-md sm:h-28 sm:w-44">
          <img
            src={article.cover}
            alt={`${article.title}配图`}
            title={`${article.title} - 色戒未删减版`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
          />
        </div>
        <div className="min-w-0 flex-1 py-1">
          <div className="mb-2 flex items-center gap-2 text-[11px] text-foreground-500">
            <span className="rounded-full bg-secondary-900/80 px-2 py-0.5 text-secondary-200">
              {meta.label}
            </span>
            <span>{formatDate(article.date)}</span>
          </div>
          <h3 className="font-heading text-base leading-snug text-foreground-50 transition-colors group-hover:text-accent-400">
            {article.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-[13px] leading-relaxed text-foreground-500">
            {article.excerpt}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-800/80 bg-background-900/60 transition-colors hover:border-primary-700/70 hover:bg-background-900"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <img
          src={article.cover}
          alt={`${article.title}配图`}
          title={`${article.title} - 色戒未删减版`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent" />
        <span className="absolute left-3 top-3 rounded-full border border-accent-500/40 bg-background-950/80 px-2.5 py-1 font-label text-[10px] uppercase tracking-[0.16em] text-accent-300 backdrop-blur-sm">
          {meta.label}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-heading text-lg leading-snug text-foreground-50 transition-colors group-hover:text-accent-400">
          {article.title}
        </h3>
        <p className="mt-2.5 line-clamp-2 flex-1 text-[13px] leading-relaxed text-foreground-500">
          {article.excerpt}
        </p>
        <div className="mt-4 flex items-center gap-3 border-t border-background-800/80 pt-3 text-[11px] text-foreground-500">
          <span>{formatDate(article.date)}</span>
          <span className="h-3 w-px bg-background-700" />
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
          <span className="h-3 w-px bg-background-700" />
          <span>{article.readTime} 分钟阅读</span>
        </div>
      </div>
    </Link>
  );
}